import {
  faShieldAlt,
  faLock,
  faExclamationTriangle,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

interface AccessDeniedOverlayProps {
  children?: React.ReactNode;
  message?: string;
  compact?: boolean;
  minHeight?: string;
}

const AccessDeniedOverlay: React.FC<AccessDeniedOverlayProps> = ({
  children,
  message = "Esta área requer permissões adicionais",
  compact = false,
  minHeight,
}) => {
  return (
    <div className="position-relative">
      {/* Blurred background content */}
      {children && (
        <div
        className="bg-body-emphasis"
          style={{
            pointerEvents: "none",
            userSelect: "none",
            borderRadius: "12px",
            overflow: "hidden",            
            minHeight: compact ? "" : minHeight || "500px",
            height: "100%",
          }}
        >
          {children}
        </div>
      )}

      {/* Access denied overlay */}
      <div
        className="position-absolute top-0 start-0 end-0 bottom-0 d-flex justify-content-center align-items-center"
        style={{
          backdropFilter: "blur(8px)",
          zIndex: 10,
          borderRadius: "16px",
        }}
      >
        <div
          className="position-relative"
          style={{
            width: compact ? "100%" : "auto",
            padding: compact ? "0.75rem" : "1rem",
            maxWidth: compact ? "none" : "500px",
          }}
        >
          {compact ? (
            // Compact version
            <div
              className="card shadow-sm border-0 overflow-hidden bg-body-emphasis"
              style={{
                borderRadius: "12px",
                background: "white",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className="position-absolute w-100"
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background:
                    "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
                }}
              ></div>

              <div className="card-body p-3">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      background: "linear-gradient(45deg, #ef4444, #f97316)",
                      width: "38px",
                      height: "38px",
                      boxShadow: "0 8px 12px -3px rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      className="text-white"
                      size="sm"
                    />
                  </div>
                  <div className="d-flex flex-column flex-grow-1">
                    <h6 className="mb-0 fw-semibold">Acesso Restrito</h6>
                    <p className="mb-0 small text-muted">{message}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Full version
            <div
              className="card shadow border-0 overflow-hidden bg-body-emphasis"
              style={{
                borderRadius: "16px",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              }}
            >
              <div
                className="position-absolute w-100"
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "6px",
                  background:
                    "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
                }}
              ></div>

              {/* Floating security graphic */}
              <div
                className="position-absolute"
                style={{
                  top: "15px",
                  right: "15px",
                  opacity: 0.05,
                }}
              >
                <FontAwesomeIcon icon={faFingerprint} className="fa-4x" />
              </div>

              <div className="card-body p-4">
                {/* Icon and title section */}
                <div className="d-flex align-items-center mb-4">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      background: "linear-gradient(45deg, #ef4444, #f97316)",
                      width: "52px",
                      height: "52px",
                      boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      className="text-white fa-lg"
                    />
                  </div>
                  <div>
                    <h4 className="mb-0 fw-bold">Acesso Restrito</h4>
                    <p className="text-muted mb-0 small">
                      Permissão Insuficiente
                    </p>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4">
                  <p className="lead mb-0">{message}</p>
                </div>

                {/* Info box */}
                <div
                  className="rounded-3 p-3 mb-0"
                  style={{
                    background: "rgba(59, 130, 246, 0.05)",
                    border: "1px solid rgba(59, 130, 246, 0.1)",
                  }}
                >
                  <div className="d-flex mb-2">
                    <div
                      className="rounded-circle me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        background: "rgba(59, 130, 246, 0.1)",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-primary"
                        size="sm"
                      />
                    </div>
                    <p className="mb-0 small">
                      Entre em contato com o administrador do sistema para obter
                      as permissões necessárias.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessDeniedOverlay;
